import { createContext, useReducer, type ReactNode } from 'react';
import type { ChainType, PageId, Broker, Account, StepStatus, OffboardingStep, AuthContext as AuthCtx } from '../types';
import { getMockBrokers } from '../data/mockData';
import { removeHoldingAmount } from '../lib/accounts';
import { isSubAtomicWithdrawalRemainder } from '../lib/withdrawal';
import { addSentryBreadcrumb } from '../sentry';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const MOCK_SCENARIO = import.meta.env.VITE_MOCK_SCENARIO as string | undefined;
const USE_NEGATIVE_USDC_MOCK = USE_MOCK && MOCK_SCENARIO === 'negative-usdc';
const NEGATIVE_USDC_MOCK_BROKER_ID = 'broker-woofi';
const NEGATIVE_USDC_MOCK_ACCOUNT_ID = '0x_mock_negative_usdc';
const MOCK_EVM_ADDRESS = '0x742d35Cc6634C0532925a3b844Bc9e7595f2bD18';

interface StepState {
  cancel_orders: StepStatus;
  close_positions: StepStatus;
  settle_pnl: StepStatus;
  convert_negative_usdc: StepStatus;
  withdraw: StepStatus;
}

export interface AccountScope {
  sessionGeneration: number;
  brokerId: string;
  accountId: string;
}

interface WalletHoldingCeiling {
  expectedHolding: number;
  submittedAt: number;
  fullWithdrawalDecimals?: number;
}

type ConnectedWallets = Record<ChainType, string | null>;

export interface AppState {
  sessionGeneration: number;
  latestAccountDataRequestIds: Record<string, number>;
  walletHoldingCeilings: Record<string, WalletHoldingCeiling>;
  page: PageId;
  chain: ChainType | null;
  preferredChain: ChainType | null;
  walletAddress: string | null;
  connectedWallets: ConnectedWallets;
  brokers: Broker[];
  selectedBrokerId: string | null;
  selectedAccountId: string | null;
  steps: StepState;
  accountSteps: Record<string, StepState>;
  stepError: string | null;
  accountStepErrors: Record<string, string | null>;
  accountStepProgress: Record<string, string | null>;
  reopenedSteps: OffboardingStep[];
  accountReopenedSteps: Record<string, OffboardingStep[]>;
  authContext: AuthCtx | null;
  loading: boolean;
  error: string | null;
  stepProgress: string | null;
}

export type AppAction =
  | { type: 'CONNECT_WALLET'; chain: ChainType; address: string }
  | { type: 'SYNC_CONNECTED_WALLET'; chain: ChainType; address: string }
  | { type: 'SET_PREFERRED_CHAIN'; chain: ChainType }
  | { type: 'SET_ACTIVE_WALLET'; chain: ChainType }
  | { type: 'DISCONNECT_CHAIN'; chain: ChainType }
  | { type: 'DISCONNECT_WALLET' }
  | { type: 'SET_BROKERS'; brokers: Broker[]; walletAddress?: string; chain?: ChainType }
  | { type: 'SELECT_BROKER'; brokerId: string; walletAddress?: string; chain?: ChainType }
  | { type: 'SELECT_ACCOUNT'; accountId: string }
  | { type: 'BACK_TO_OVERVIEW' }
  | { type: 'BACK_TO_BROKERS' }
  | ({ type: 'START_STEP'; step: OffboardingStep } & AccountScope)
  | ({ type: 'COMPLETE_STEP'; step: OffboardingStep } & AccountScope)
  | ({ type: 'SKIP_STEP'; step: OffboardingStep } & AccountScope)
  | ({ type: 'STEP_ERROR'; step: OffboardingStep; error: string } & AccountScope)
  | ({ type: 'RESET_STEPS' } & AccountScope)
  | ({ type: 'CANCEL_STEP'; step: OffboardingStep } & AccountScope)
  | { type: 'SET_AUTH_CONTEXT'; auth: AuthCtx; walletAddress: string; chain: ChainType }
  | { type: 'CLEAR_AUTH_CONTEXT' }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | ({ type: 'SET_STEP_PROGRESS'; message: string | null } & AccountScope)
  | ({ type: 'APPLY_CONVERT_TO_USDC_RESULT'; token: string; collateralHolding: number; usdcBalance: number } & AccountScope)
  | ({ type: 'WITHDRAW_ASSET'; token: string; amount: number; requestBarrierId?: number; fullWithdrawalDecimals?: number } & AccountScope)
  | ({ type: 'WALLET_WITHDRAWAL_SUBMITTED'; token: string; expectedHolding: number; submittedAt: number; requestBarrierId: number; fullWithdrawalDecimals?: number } & AccountScope)
  | ({
      type: 'UPDATE_ACCOUNT_DATA';
      requestId: number;
      brokerId: string;
      accounts: Account[];
      walletAddress: string;
      chain: ChainType;
      authAccountId: string;
      orderlyKey: string;
    } & Pick<AccountScope, 'sessionGeneration'>);

const initialSteps: StepState = {
  cancel_orders: 'pending',
  close_positions: 'pending',
  settle_pnl: 'pending',
  convert_negative_usdc: 'pending',
  withdraw: 'pending',
};

// Exported for reducer unit tests. Not a component export; fast-refresh does not apply.
// eslint-disable-next-line react-refresh/only-export-components
export const initialState: AppState = {
  sessionGeneration: 0,
  latestAccountDataRequestIds: {},
  walletHoldingCeilings: {},
  page: USE_NEGATIVE_USDC_MOCK ? 'account_action' : 'connect',
  chain: USE_NEGATIVE_USDC_MOCK ? 'evm' : null,
  preferredChain: USE_NEGATIVE_USDC_MOCK ? 'evm' : null,
  walletAddress: USE_NEGATIVE_USDC_MOCK ? MOCK_EVM_ADDRESS : null,
  connectedWallets: {
    evm: USE_NEGATIVE_USDC_MOCK ? MOCK_EVM_ADDRESS : null,
    solana: null,
  },
  brokers: getInitialBrokers(),
  selectedBrokerId: USE_NEGATIVE_USDC_MOCK ? NEGATIVE_USDC_MOCK_BROKER_ID : null,
  selectedAccountId: USE_NEGATIVE_USDC_MOCK ? NEGATIVE_USDC_MOCK_ACCOUNT_ID : null,
  steps: { ...initialSteps },
  accountSteps: {},
  stepError: null,
  accountStepErrors: {},
  accountStepProgress: {},
  reopenedSteps: [],
  accountReopenedSteps: {},
  authContext: null,
  loading: false,
  error: null,
  stepProgress: null,
};

function getScopedAccount(state: AppState, brokerId: string, accountId: string): Account | null {
  const broker = state.brokers.find((b) => b.id === brokerId);
  if (!broker) return null;
  return broker.accounts.find((a) => a.accountId === accountId) ?? null;
}

function updateAccount(state: AppState, brokerId: string, accountId: string, updater: (a: Account) => Account): Broker[] {
  return state.brokers.map((broker) => (
    broker.id === brokerId
      ? { ...broker, accounts: broker.accounts.map((account) => account.accountId === accountId ? updater(account) : account) }
      : broker
  ));
}

function getAccountKey(brokerId: string, accountId: string): string {
  return `${brokerId}/${accountId}`;
}

function getHoldingCeilingKey(sessionGeneration: number, brokerId: string, accountId: string, token: string): string {
  return `${sessionGeneration}/${brokerId}/${accountId}/${token}`;
}

function isSelectedScope(state: AppState, scope: Pick<AccountScope, 'brokerId' | 'accountId'>): boolean {
  return state.selectedBrokerId === scope.brokerId && state.selectedAccountId === scope.accountId;
}

function isValidAccountScope(state: AppState, scope: AccountScope): boolean {
  return state.sessionGeneration === scope.sessionGeneration && getScopedAccount(state, scope.brokerId, scope.accountId) !== null;
}

function getScopeSteps(state: AppState, scope: Pick<AccountScope, 'brokerId' | 'accountId'>): StepState {
  return state.accountSteps[getAccountKey(scope.brokerId, scope.accountId)]
    ?? (isSelectedScope(state, scope) ? state.steps : initialSteps);
}

function updateScopedStepState(
  state: AppState,
  scope: AccountScope,
  steps: StepState,
  options: { error?: string | null; progress?: string | null; reopenedSteps?: OffboardingStep[] } = {},
): Partial<AppState> {
  const key = getAccountKey(scope.brokerId, scope.accountId);
  const selected = isSelectedScope(state, scope);
  const currentError = key in state.accountStepErrors ? state.accountStepErrors[key] : selected ? state.stepError : null;
  const currentProgress = key in state.accountStepProgress ? state.accountStepProgress[key] : selected ? state.stepProgress : null;
  const currentReopenedSteps = state.accountReopenedSteps[key] ?? (selected ? state.reopenedSteps : []);
  const nextError = options.error === undefined ? currentError : options.error;
  const nextProgress = options.progress === undefined ? currentProgress : options.progress;
  const nextReopenedSteps = options.reopenedSteps ?? currentReopenedSteps;

  return {
    accountSteps: { ...state.accountSteps, [key]: steps },
    accountStepErrors: { ...state.accountStepErrors, [key]: nextError },
    accountStepProgress: { ...state.accountStepProgress, [key]: nextProgress },
    accountReopenedSteps: { ...state.accountReopenedSteps, [key]: nextReopenedSteps },
    ...(selected ? { steps, stepError: nextError, stepProgress: nextProgress, reopenedSteps: nextReopenedSteps } : {}),
  };
}

function applyUsdcDelta(account: Account, delta: number): Account {
  const existing = account.holdings.find((holding) => holding.token === 'USDC');
  const holdings = existing
    ? account.holdings.map((holding) => (holding.token === 'USDC' ? { ...holding, holding: holding.holding + delta } : holding))
    : [...account.holdings, { token: 'USDC', holding: delta, frozen: 0 }];

  return {
    ...account,
    holdings,
    usdcBalance: account.usdcBalance + delta,
  };
}

function setHolding(account: Account, token: string, holding: number): Account {
  const existing = account.holdings.find((entry) => entry.token === token);
  const holdings = existing
    ? account.holdings.map((entry) => (entry.token === token ? { ...entry, holding } : entry))
    : [...account.holdings, { token, holding, frozen: 0 }];

  return { ...account, holdings };
}

function setUsdcBalance(account: Account, usdcBalance: number): Account {
  return { ...setHolding(account, 'USDC', usdcBalance), usdcBalance };
}

function getNextActiveWallet(connectedWallets: ConnectedWallets): { chain: ChainType; address: string } | null {
  if (connectedWallets.solana) {
    return { chain: 'solana', address: connectedWallets.solana };
  }

  if (connectedWallets.evm) {
    return { chain: 'evm', address: connectedWallets.evm };
  }

  return null;
}

function getInitialBrokers(): Broker[] {
  return USE_MOCK ? getMockBrokers(MOCK_SCENARIO) : [];
}

function resetBrokerSessionState() {
  return {
    brokers: getInitialBrokers(),
    walletHoldingCeilings: {},
    selectedBrokerId: null,
    selectedAccountId: null,
    steps: { ...initialSteps },
    accountSteps: {},
    stepError: null,
    accountStepErrors: {},
    accountStepProgress: {},
    reopenedSteps: [],
    accountReopenedSteps: {},
    authContext: null,
    stepProgress: null,
  };
}

// Exported for reducer unit tests. Not a component export; fast-refresh does not apply.
// eslint-disable-next-line react-refresh/only-export-components
export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'CONNECT_WALLET': {
      const activeWalletChanged = state.chain !== action.chain || state.walletAddress !== action.address;
      return {
        ...state,
        ...(activeWalletChanged ? resetBrokerSessionState() : {}),
        sessionGeneration: state.sessionGeneration + (activeWalletChanged ? 1 : 0),
        chain: action.chain,
        preferredChain: action.chain,
        walletAddress: action.address,
        connectedWallets: {
          ...state.connectedWallets,
          [action.chain]: action.address,
        },
        page: state.page === 'connect' || activeWalletChanged ? 'select_broker' : state.page,
        error: null,
      };
    }

    case 'SYNC_CONNECTED_WALLET': {
      const connectedWallets = {
        ...state.connectedWallets,
        [action.chain]: action.address,
      };

      const shouldActivate = !state.walletAddress || state.chain === action.chain || state.preferredChain === action.chain;

      addSentryBreadcrumb('Wallet connected', 'navigation', {
        chain: action.chain,
        address: action.address.slice(0, 10) + '...',
      });

      const activeWalletChanged = shouldActivate && (state.chain !== action.chain || state.walletAddress !== action.address);

      return {
        ...state,
        ...(activeWalletChanged ? resetBrokerSessionState() : {}),
        sessionGeneration: state.sessionGeneration + (activeWalletChanged ? 1 : 0),
        connectedWallets,
        chain: shouldActivate ? action.chain : state.chain,
        walletAddress: shouldActivate ? action.address : state.walletAddress,
        page: (!state.walletAddress && state.page === 'connect') || activeWalletChanged ? 'select_broker' : state.page,
        error: null,
      };
    }

    case 'SET_PREFERRED_CHAIN':
      return {
        ...state,
        preferredChain: action.chain,
      };

    case 'SET_ACTIVE_WALLET': {
      const address = state.connectedWallets[action.chain];
      if (!address) return state;

      const activeWalletChanged = state.chain !== action.chain || state.walletAddress !== address;

      return {
        ...state,
        ...(activeWalletChanged ? resetBrokerSessionState() : {}),
        sessionGeneration: state.sessionGeneration + (activeWalletChanged ? 1 : 0),
        chain: action.chain,
        preferredChain: action.chain,
        walletAddress: address,
        page: state.page === 'connect' || activeWalletChanged ? 'select_broker' : state.page,
        error: null,
      };
    }

    case 'DISCONNECT_CHAIN': {
      const connectedWallets = {
        ...state.connectedWallets,
        [action.chain]: null,
      };

      const nextActiveWallet = getNextActiveWallet(connectedWallets);
      if (!nextActiveWallet) {
        return {
          ...initialState,
          sessionGeneration: state.sessionGeneration + 1,
          walletHoldingCeilings: {},
          brokers: getInitialBrokers(),
        };
      }

      if (state.chain !== action.chain) {
        return {
          ...state,
          sessionGeneration: state.sessionGeneration + 1,
          walletHoldingCeilings: {},
          connectedWallets,
          preferredChain: state.preferredChain === action.chain ? state.chain : state.preferredChain,
          error: null,
        };
      }

      return {
        ...state,
        ...resetBrokerSessionState(),
        sessionGeneration: state.sessionGeneration + 1,
        connectedWallets,
        chain: nextActiveWallet.chain,
        preferredChain: state.preferredChain === action.chain ? nextActiveWallet.chain : state.preferredChain,
        walletAddress: nextActiveWallet.address,
        page: 'select_broker',
        error: null,
      };
    }

    case 'DISCONNECT_WALLET':
      return {
        ...initialState,
        sessionGeneration: state.sessionGeneration + 1,
        brokers: getInitialBrokers(),
      };

    case 'SET_BROKERS': {
      if (action.walletAddress && state.walletAddress !== action.walletAddress) return state;
      if (action.chain && state.chain !== action.chain) return state;
      return { ...state, brokers: action.brokers };
    }

    case 'SELECT_BROKER': {
      if (action.walletAddress && state.walletAddress !== action.walletAddress) return state;
      if (action.chain && state.chain !== action.chain) return state;
      const brokerSessionChanged = state.selectedBrokerId !== action.brokerId;
      addSentryBreadcrumb('Broker selected', 'navigation', { broker_id: action.brokerId });
      return {
        ...state,
        sessionGeneration: state.sessionGeneration + (brokerSessionChanged ? 1 : 0),
        selectedBrokerId: action.brokerId,
        selectedAccountId: null,
        page: 'account_overview',
        steps: { ...initialSteps },
        accountSteps: {},
        walletHoldingCeilings: brokerSessionChanged ? {} : state.walletHoldingCeilings,
        authContext: state.authContext?.brokerId === action.brokerId ? state.authContext : null,
        stepProgress: null,
        error: null,
      };
    }

    case 'SELECT_ACCOUNT': {
      if (!state.selectedBrokerId) return state;
      const previousKey = state.selectedAccountId ? getAccountKey(state.selectedBrokerId, state.selectedAccountId) : null;
      const accountSteps = previousKey
        ? { ...state.accountSteps, [previousKey]: state.steps }
        : state.accountSteps;
      const accountStepErrors = previousKey
        ? { ...state.accountStepErrors, [previousKey]: state.stepError }
        : state.accountStepErrors;
      const accountStepProgress = previousKey
        ? { ...state.accountStepProgress, [previousKey]: state.stepProgress }
        : state.accountStepProgress;
      const accountReopenedSteps = previousKey
        ? { ...state.accountReopenedSteps, [previousKey]: state.reopenedSteps }
        : state.accountReopenedSteps;
      const key = getAccountKey(state.selectedBrokerId, action.accountId);
      const existingSteps = accountSteps[key];
      addSentryBreadcrumb('Account selected', 'navigation', { account_id: action.accountId.slice(0, 10) + '...' });
      return {
        ...state,
        selectedAccountId: action.accountId,
        page: 'account_action',
        steps: { ...initialSteps, ...(existingSteps ?? {}) },
        accountSteps,
        stepError: accountStepErrors[key] ?? null,
        accountStepErrors,
        stepProgress: accountStepProgress[key] ?? null,
        accountStepProgress,
        reopenedSteps: accountReopenedSteps[key] ?? [],
        accountReopenedSteps,
        error: null,
      };
    }

    case 'BACK_TO_OVERVIEW': {
      if (!state.selectedBrokerId || !state.selectedAccountId) {
        return { ...state, selectedAccountId: null, page: 'account_overview', stepError: null, stepProgress: null, reopenedSteps: [] };
      }
      const key = getAccountKey(state.selectedBrokerId, state.selectedAccountId);
      return {
        ...state,
        selectedAccountId: null,
        page: 'account_overview',
        accountSteps: { ...state.accountSteps, [key]: state.steps },
        accountStepErrors: { ...state.accountStepErrors, [key]: state.stepError },
        accountStepProgress: { ...state.accountStepProgress, [key]: state.stepProgress },
        accountReopenedSteps: { ...state.accountReopenedSteps, [key]: state.reopenedSteps },
        stepError: null,
        stepProgress: null,
        reopenedSteps: [],
      };
    }

    case 'BACK_TO_BROKERS':
      return {
        ...state,
        ...resetBrokerSessionState(),
        sessionGeneration: state.sessionGeneration + 1,
        page: 'select_broker',
      };

    case 'START_STEP': {
      if (!isValidAccountScope(state, action)) return state;
      addSentryBreadcrumb('Offboarding step started', 'action', { step: action.step });
      const steps = { ...getScopeSteps(state, action), [action.step]: 'in_progress' } as StepState;
      const reopenedSteps = (state.accountReopenedSteps[getAccountKey(action.brokerId, action.accountId)] ?? [])
        .filter((step) => step !== action.step);
      return { ...state, ...updateScopedStepState(state, action, steps, { error: null, reopenedSteps }) };
    }

    case 'COMPLETE_STEP': {
      if (!isValidAccountScope(state, action)) return state;
      const newSteps = { ...getScopeSteps(state, action), [action.step]: 'completed' as StepStatus };

      let newBrokers = state.brokers;
      switch (action.step) {
        case 'cancel_orders':
          newBrokers = updateAccount(state, action.brokerId, action.accountId, (a) => ({ ...a, pendingOrders: [] }));
          break;
        case 'close_positions':
          newBrokers = updateAccount(state, action.brokerId, action.accountId, (a) => ({
            ...applyUsdcDelta(a, a.positions.reduce((sum, p) => sum + p.unrealizedPnl, 0)),
            positions: [],
          }));
          break;
        case 'settle_pnl':
          newBrokers = updateAccount(state, action.brokerId, action.accountId, (a) => ({
            ...applyUsdcDelta(a, a.unsettledPnl),
            unsettledPnl: 0,
          }));
          break;
        case 'withdraw': {
          const account = getScopedAccount(state, action.brokerId, action.accountId);
          if (account?.type === 'sub') {
            const mainAccount = state.brokers
              .find((b) => b.id === action.brokerId)
              ?.accounts.find((a) => a.type === 'main');
            if (mainAccount) {
              newBrokers = updateAccount(
                { ...state, brokers: updateAccount(state, action.brokerId, action.accountId, (a) => ({ ...a, usdcBalance: 0, totalAssets: 0 })) },
                action.brokerId,
                mainAccount.accountId,
                (a) => ({
                  ...a,
                  usdcBalance: a.usdcBalance + (account.usdcBalance),
                  totalAssets: a.totalAssets + (account.usdcBalance),
                })
              );
            }
          } else {
            newBrokers = updateAccount(state, action.brokerId, action.accountId, (a) => ({ ...a, usdcBalance: 0, totalAssets: 0 }));
          }
          break;
        }
      }
      return { ...state, brokers: newBrokers, ...updateScopedStepState(state, action, newSteps, { error: null, progress: null }) };
    }

    case 'SKIP_STEP': {
      if (!isValidAccountScope(state, action)) return state;
      const steps = { ...getScopeSteps(state, action), [action.step]: 'skipped' as StepStatus };
      return { ...state, ...updateScopedStepState(state, action, steps) };
    }

    case 'STEP_ERROR': {
      if (!isValidAccountScope(state, action)) return state;
      addSentryBreadcrumb('Offboarding step error', 'error', { step: action.step, error: action.error.slice(0, 100) }, 'error');
      const steps = { ...getScopeSteps(state, action), [action.step]: 'error' } as StepState;
      return { ...state, ...updateScopedStepState(state, action, steps, { error: action.error, progress: null }) };
    }

    case 'RESET_STEPS': {
      if (!isValidAccountScope(state, action)) return state;
      return { ...state, ...updateScopedStepState(state, action, { ...initialSteps }, { error: null, progress: null, reopenedSteps: [] }) };
    }

    case 'CANCEL_STEP': {
      if (!isValidAccountScope(state, action)) return state;
      const steps = { ...getScopeSteps(state, action), [action.step]: 'pending' } as StepState;
      return { ...state, ...updateScopedStepState(state, action, steps, { error: null, progress: null }) };
    }

    case 'SET_AUTH_CONTEXT': {
      if (state.walletAddress !== action.walletAddress || state.chain !== action.chain) return state;
      const authChanged = !state.authContext
        || state.authContext.accountId !== action.auth.accountId
        || state.authContext.brokerId !== action.auth.brokerId
        || state.authContext.orderlyKey !== action.auth.orderlyKey;
      return {
        ...state,
        authContext: action.auth,
        sessionGeneration: state.sessionGeneration + (authChanged ? 1 : 0),
        walletHoldingCeilings: authChanged ? {} : state.walletHoldingCeilings,
      };
    }

    case 'CLEAR_AUTH_CONTEXT':
      return {
        ...state,
        authContext: null,
        sessionGeneration: state.sessionGeneration + (state.authContext ? 1 : 0),
        walletHoldingCeilings: state.authContext ? {} : state.walletHoldingCeilings,
      };

    case 'SET_LOADING':
      return { ...state, loading: action.loading };

    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'SET_STEP_PROGRESS': {
      if (!isValidAccountScope(state, action)) return state;
      return {
        ...state,
        ...updateScopedStepState(state, action, getScopeSteps(state, action), { progress: action.message }),
      };
    }

    case 'APPLY_CONVERT_TO_USDC_RESULT': {
      if (!isValidAccountScope(state, action)) return state;

      // Reconcile against the server-confirmed balances captured at settlement:
      // set the converted collateral to its actual remaining holding (not an optimistic
      // "requested amount" deduction) and the USDC balance to the confirmed value.
      const newBrokers = updateAccount(state, action.brokerId, action.accountId, (a) => (
        setUsdcBalance(setHolding(a, action.token, action.collateralHolding), action.usdcBalance)
      ));

      return {
        ...state,
        brokers: newBrokers,
        ...updateScopedStepState(state, action, getScopeSteps(state, action), { error: null, progress: null }),
      };
    }

    case 'WITHDRAW_ASSET': {
      if (!isValidAccountScope(state, action)) return state;

      const requestScopeKey = `${action.sessionGeneration}/${action.brokerId}`;

      const selectedBroker = state.brokers.find((b) => b.id === action.brokerId);
      const sourceAccount = selectedBroker?.accounts.find((a) => a.accountId === action.accountId);
      if (!sourceAccount) return state;

      const sourceHolding = sourceAccount.holdings.find((holding) => holding.token === action.token);
      const remainingAvailable = sourceHolding
        ? sourceHolding.holding - (sourceHolding.frozen ?? 0) - action.amount
        : 0;
      const amountToRemove = action.fullWithdrawalDecimals !== undefined
        && isSubAtomicWithdrawalRemainder(remainingAvailable, action.fullWithdrawalDecimals)
        ? action.amount + remainingAvailable
        : action.amount;
      const updatedSourceAccount = removeHoldingAmount(sourceAccount, action.token, amountToRemove);
      const updatedSourceHoldings = updatedSourceAccount.holdings;
      const sourceHasRemainingAssets = updatedSourceHoldings.some((h) => h.holding > 0);

      let newBrokers = updateAccount(state, action.brokerId, action.accountId, (a) => ({
        ...removeHoldingAmount(a, action.token, amountToRemove),
        totalAssets: sourceHasRemainingAssets || a.positions.length > 0 || a.unsettledPnl !== 0 ? a.totalAssets : 0,
      }));

      if (sourceAccount.type === 'sub') {
        const mainAccount = selectedBroker?.accounts.find((a) => a.type === 'main');
        if (mainAccount) {
          newBrokers = updateAccount({ ...state, brokers: newBrokers }, action.brokerId, mainAccount.accountId, (a) => {
            const existing = a.holdings.find((h) => h.token === action.token);
            const holdings = existing
              ? a.holdings.map((h) => (h.token === action.token ? { ...h, holding: h.holding + action.amount } : h))
              : [...a.holdings, { token: action.token, holding: action.amount, frozen: 0 }];

            return {
              ...a,
              holdings,
              usdcBalance: action.token === 'USDC' ? a.usdcBalance + action.amount : a.usdcBalance,
              totalAssets: action.token === 'USDC' ? a.totalAssets + action.amount : a.totalAssets,
            };
          });
        }
      }

      const steps = { ...getScopeSteps(state, action), withdraw: sourceHasRemainingAssets ? 'pending' : 'completed' } as StepState;

      return {
        ...state,
        ...(action.requestBarrierId === undefined ? {} : {
          latestAccountDataRequestIds: {
            ...state.latestAccountDataRequestIds,
            [requestScopeKey]: Math.max(
              state.latestAccountDataRequestIds[requestScopeKey] ?? 0,
              action.requestBarrierId,
            ),
          },
        }),
        brokers: newBrokers,
        ...updateScopedStepState(state, action, steps, { error: null, progress: null }),
      };
    }

    case 'WALLET_WITHDRAWAL_SUBMITTED': {
      if (!isValidAccountScope(state, action)) return state;

      const requestScopeKey = `${action.sessionGeneration}/${action.brokerId}`;
      const ceilingKey = getHoldingCeilingKey(action.sessionGeneration, action.brokerId, action.accountId, action.token);
      const sourceAccount = getScopedAccount(state, action.brokerId, action.accountId);
      if (!sourceAccount) return state;

      const currentHolding = sourceAccount.holdings.find((holding) => holding.token === action.token)?.holding ?? 0;
      const expectedHolding = Math.min(currentHolding, action.expectedHolding);
      const updatedAccount = setHolding(sourceAccount, action.token, expectedHolding);
      const sourceHasRemainingAssets = updatedAccount.holdings.some((holding) => holding.holding > 0);
      const newBrokers = updateAccount(state, action.brokerId, action.accountId, (account) => ({
        ...setHolding(account, action.token, expectedHolding),
        usdcBalance: action.token === 'USDC' ? Math.min(account.usdcBalance, expectedHolding) : account.usdcBalance,
        totalAssets: sourceHasRemainingAssets || account.positions.length > 0 || account.unsettledPnl !== 0 ? account.totalAssets : 0,
      }));
      const steps = { ...getScopeSteps(state, action), withdraw: sourceHasRemainingAssets ? 'pending' : 'completed' } as StepState;

      return {
        ...state,
        latestAccountDataRequestIds: {
          ...state.latestAccountDataRequestIds,
          [requestScopeKey]: Math.max(state.latestAccountDataRequestIds[requestScopeKey] ?? 0, action.requestBarrierId),
        },
        walletHoldingCeilings: {
          ...state.walletHoldingCeilings,
          [ceilingKey]: {
            expectedHolding: Math.min(
              state.walletHoldingCeilings[ceilingKey]?.expectedHolding ?? Number.POSITIVE_INFINITY,
              action.expectedHolding,
            ),
            submittedAt: action.submittedAt,
            fullWithdrawalDecimals: action.fullWithdrawalDecimals,
          },
        },
        brokers: newBrokers,
        ...updateScopedStepState(state, action, steps, { error: null, progress: null }),
      };
    }

    case 'UPDATE_ACCOUNT_DATA': {
      if (state.sessionGeneration !== action.sessionGeneration) return state;
      const requestScopeKey = `${action.sessionGeneration}/${action.brokerId}`;
      const latestRequestId = state.latestAccountDataRequestIds[requestScopeKey];
      if (latestRequestId !== undefined && action.requestId < latestRequestId) return state;
      const authContext = state.authContext;
      if (
        state.walletAddress !== action.walletAddress
        || state.chain !== action.chain
        || !authContext
        || authContext.brokerId !== action.brokerId
        || authContext.accountId !== action.authAccountId
        || authContext.orderlyKey !== action.orderlyKey
      ) {
        return state;
      }

      let walletHoldingCeilings = state.walletHoldingCeilings;
      const accounts = action.accounts.map((account) => {
        const accountPrefix = `${action.sessionGeneration}/${action.brokerId}/${account.accountId}/`;
        const accountCeilings = Object.entries(walletHoldingCeilings).filter(([key]) => key.startsWith(accountPrefix));
        if (accountCeilings.length === 0) return account;

        let holdings = account.holdings;
        let usdcBalance = account.usdcBalance;
        for (const [key, ceiling] of accountCeilings) {
          const token = key.slice(accountPrefix.length);
          const incoming = account.holdings.find((holding) => holding.token === token);
          const atomicUnit = ceiling.fullWithdrawalDecimals === undefined ? 0 : 10 ** -ceiling.fullWithdrawalDecimals;
          const normalizeDust = atomicUnit > 0 && ceiling.expectedHolding < atomicUnit && (incoming?.holding ?? 0) < atomicUnit;
          const updatedTime = incoming?.updatedTime;
          const updatedTimeMs = updatedTime !== undefined && Number.isFinite(updatedTime) && updatedTime > 0
            ? updatedTime < 1e12 ? updatedTime * 1000 : updatedTime
            : undefined;
          if (!normalizeDust && (!incoming || incoming.holding <= ceiling.expectedHolding
            || (updatedTimeMs !== undefined && updatedTimeMs >= ceiling.submittedAt))) {
            if (walletHoldingCeilings === state.walletHoldingCeilings) walletHoldingCeilings = { ...state.walletHoldingCeilings };
            delete walletHoldingCeilings[key];
          } else {
            if (holdings === account.holdings) holdings = [...account.holdings];
            holdings = holdings.map((holding) => holding.token === token ? { ...holding, holding: ceiling.expectedHolding } : holding);
            if (token === 'USDC') usdcBalance = Math.min(usdcBalance, ceiling.expectedHolding);
          }
        }
        return holdings === account.holdings ? account : { ...account, holdings, usdcBalance };
      });

      let accountSteps = state.accountSteps;
      let accountReopenedSteps = state.accountReopenedSteps;
      let visibleSteps = state.steps;
      let visibleReopenedSteps = state.reopenedSteps;

      for (const account of accounts) {
        const scope = { brokerId: action.brokerId, accountId: account.accountId };
        const key = getAccountKey(scope.brokerId, scope.accountId);
        const currentSteps = state.accountSteps[key] ?? (isSelectedScope(state, scope) ? state.steps : initialSteps);
        const signals: Array<[OffboardingStep, boolean]> = [
          ['cancel_orders', account.pendingOrders.length > 0],
          ['close_positions', account.positions.length > 0],
          ['settle_pnl', account.unsettledPnl !== 0],
          ['convert_negative_usdc', account.usdcBalance < 0 || account.holdings.some((holding) => holding.token === 'USDC' && holding.holding < 0)],
          ['withdraw', account.holdings.some((holding) => holding.holding > 0)],
        ];
        let nextSteps = currentSteps;
        const currentReopened = state.accountReopenedSteps[key] ?? [];
        let reopened = currentReopened;

        for (const [step, hasFreshActivity] of signals) {
          if (hasFreshActivity && (nextSteps[step] === 'completed' || nextSteps[step] === 'skipped')) {
            nextSteps = { ...nextSteps, [step]: 'pending' };
            if (!reopened.includes(step)) reopened = [...reopened, step];
          }
        }

        if (nextSteps !== currentSteps) {
          if (accountSteps === state.accountSteps) accountSteps = { ...state.accountSteps };
          accountSteps[key] = nextSteps;
        }
        if (reopened !== currentReopened) {
          if (accountReopenedSteps === state.accountReopenedSteps) accountReopenedSteps = { ...state.accountReopenedSteps };
          accountReopenedSteps[key] = reopened;
        }
        if (isSelectedScope(state, scope)) {
          visibleSteps = nextSteps;
          visibleReopenedSteps = reopened;
        }
      }

      return {
        ...state,
        latestAccountDataRequestIds: {
          ...state.latestAccountDataRequestIds,
          [requestScopeKey]: action.requestId,
        },
        walletHoldingCeilings,
        steps: visibleSteps,
        accountSteps,
        reopenedSteps: visibleReopenedSteps,
        accountReopenedSteps,
        brokers: state.brokers.map((b) =>
          b.id === action.brokerId ? { ...b, accounts } : b
        ),
      };
    }

    default:
      return state;
  }
}

const AppContext = createContext<{ state: AppState; dispatch: React.Dispatch<AppAction> } | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
}

export { AppContext };
